import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import { safeHtml } from "utils/safeHtml";
import FileIcon from "components/FileIcon";
import { parseEml } from "./emlParser";
import type { ParsedEml, EmlAttachment } from "./emlParser";
import type { PreviewTarget } from "../FilePreviewDialog";
import { formatSize } from "./formatSize";

interface EmlPreviewProps {
  buffer: ArrayBuffer;
  /** Apre un allegato annidato nell'anteprima del chiamante. */
  onPreviewFile: (target: PreviewTarget) => void;
}

const EmlPreview: React.FC<EmlPreviewProps> = ({ buffer, onPreviewFile }) => {
  const translations = useTranslations();
  const [data, setData] = useState<ParsedEml | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    parseEml(buffer)
      .then((parsed) => {
        if (!cancelled) setData(parsed);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [buffer]);

  const download = (att: EmlAttachment) => {
    const url = URL.createObjectURL(new Blob([att.content as BlobPart], { type: att.mimeType }));
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (failed) {
    // Email non parsabile: mostro il contenuto grezzo, il download resta nel header del dialog.
    let raw = "";
    try {
      raw = new TextDecoder().decode(buffer);
    } catch {
      raw = "";
    }
    return (
      <Box sx={{ p: 2, overflow: "auto", width: "100%" }}>
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{raw}</pre>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" flex={1} minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  const headerRow = (key: string, value?: string) =>
    value ? (
      <TableRow key={key}>
        <TableCell sx={{ fontWeight: 700, width: 120, verticalAlign: "top", border: 0 }}>
          {getTranslation(translations, `preview.eml.header.${key}`) || key}
        </TableCell>
        <TableCell sx={{ border: 0, wordBreak: "break-word" }}>{value}</TableCell>
      </TableRow>
    ) : null;

  return (
    <Box sx={{ p: 2, overflow: "auto", width: "100%" }}>
      <Table size="small" sx={{ mb: 2 }}>
        <TableBody>
          {headerRow("from", data.from)}
          {headerRow("to", data.to)}
          {headerRow("cc", data.cc)}
          {headerRow("date", data.date)}
          {headerRow("subject", data.subject)}
        </TableBody>
      </Table>

      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: "background.paper",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          overflow: "auto",
        }}
      >
        {data.html ? (
          // ponytail: le risorse remote (immagini/CSS/tracking) NON sono bloccate — scelta esplicita.
          <div dangerouslySetInnerHTML={{ __html: safeHtml(data.html) }} />
        ) : data.text ? (
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>{data.text}</pre>
        ) : (
          <Typography color="text.secondary">
            {getTranslation(translations, "preview.eml.no_body") || "Nessun corpo del messaggio."}
          </Typography>
        )}
      </Box>

      {data.attachments.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            {getTranslation(translations, "preview.eml.attachments.title") || "Allegati"} ({data.attachments.length})
          </Typography>
          <List dense>
            {data.attachments.map((att, i) => (
              <ListItem
                key={i}
                disablePadding
                secondaryAction={
                  <Tooltip title={getTranslation(translations, "preview.eml.attachment.download") || "Scarica"}>
                    <IconButton size="medium" sx={{ p: 0.25 }} onClick={() => download(att)}>
                      <Download fontSize="inherit" color="primary" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <Tooltip title={getTranslation(translations, "preview.eml.attachment.preview") || "Anteprima"}>
                  <ListItemAvatar
                    onClick={() =>
                      onPreviewFile({
                        name: att.filename,
                        load: async () =>
                          new File([att.content as BlobPart], att.filename, { type: att.mimeType }),
                      })
                    }
                    sx={{ cursor: "pointer" }}
                  >
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: "#54bdc1",
                        color: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        "& .MuiSvgIcon-root": { fontSize: "2rem" },
                      }}
                    >
                      <FileIcon filename={att.filename} />
                    </Avatar>
                  </ListItemAvatar>
                </Tooltip>
                <ListItemText primary={att.filename} secondary={formatSize(att.size)} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};

export default EmlPreview;
